import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, AuthUser } from '../services/authService';
import { pinService } from '../services/pinService';
import './AdminPage.css';

interface Committee {
  id: number;
  name: string;
  displayOrder: number;
  isActive: boolean;
  memberCount: number;
}

interface Member {
  id: number;
  personId: number;
  name: string;
  role: string;
  displayOrder: number;
}

interface CommitteeDetail {
  id: number;
  name: string;
  displayOrder: number;
  isActive: boolean;
  chair: string | null;
  coChair: string | null;
  members: Member[];
}

const AdminPage: React.FC = () => {
  const [committees, setCommittees] = useState<Committee[]>([]);
  const [selectedCommittee, setSelectedCommittee] = useState<CommitteeDetail | null>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [uploadMessage, setUploadMessage] = useState<string>('');
  const [editingMember, setEditingMember] = useState<number | null>(null);
  const [editingMemberName, setEditingMemberName] = useState<number | null>(null);
  const [editMemberNameValue, setEditMemberNameValue] = useState<string>('');
  const [editingCommittee, setEditingCommittee] = useState<boolean>(false);
  const [editCommitteeName, setEditCommitteeName] = useState<string>('');
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [pin, setPin] = useState<string>('');
  const [confirmPin, setConfirmPin] = useState<string>('');
  const [pinMessage, setPinMessage] = useState<string>('');
  const [showPinSection, setShowPinSection] = useState<boolean>(false);
  const [showAddCommittee, setShowAddCommittee] = useState<boolean>(false);
  const [newCommitteeName, setNewCommitteeName] = useState<string>('');
  const [showAddMember, setShowAddMember] = useState<boolean>(false);
  const [newMemberName, setNewMemberName] = useState<string>('');
  const [newMemberRole, setNewMemberRole] = useState<string>('Member');
  const [actionMessage, setActionMessage] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    loadCommittees();
    loadAuthUser();
  }, []);

  const loadAuthUser = async () => {
    try {
      const status = await authService.getStatus();
      if (status.user) {
        setAuthUser(status.user);
      }
    } catch (error) {
      console.error('Error loading auth status:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const loadCommittees = async () => {
    try {
      const response = await fetch('/api/committees');
      const data = await response.json();
      setCommittees(data);
    } catch (error) {
      console.error('Error loading committees:', error);
    }
  };

  const loadCommitteeDetails = async (id: number) => {
    try {
      const response = await fetch(`/api/committees/${id}`);
      const data = await response.json();
      setSelectedCommittee(data);
    } catch (error) {
      console.error('Error loading committee details:', error);
    }
  };

  const handleCsvUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!csvFile) return;

    const formData = new FormData();
    formData.append('file', csvFile);

    try {
      const response = await fetch('/api/admin/import-csv', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();
      if (result.success) {
        setUploadMessage(`Success! Imported ${result.successCount} records.`);
        loadCommittees();
        setCsvFile(null);
      } else {
        setUploadMessage(`Errors: ${result.errors.join(', ')}`);
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      setUploadMessage('Error uploading file');
    }
  };

  const moveCommittee = async (id: number, direction: 'up' | 'down') => {
    const committee = committees.find(c => c.id === id);
    if (!committee) return;

    const newOrder = direction === 'up' ? committee.displayOrder - 1 : committee.displayOrder + 1;
    if (newOrder < 1 || newOrder > committees.length) return;

    try {
      await fetch(`/api/committees/${id}/order`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, newOrder }),
      });
      loadCommittees();
    } catch (error) {
      console.error('Error moving committee:', error);
    }
  };

  const moveMember = async (membershipId: number, direction: 'up' | 'down') => {
    if (!selectedCommittee) return;
    
    const member = selectedCommittee.members.find(m => m.id === membershipId);
    if (!member) return;

    const newOrder = direction === 'up' ? member.displayOrder - 1 : member.displayOrder + 1;
    if (newOrder < 1 || newOrder > selectedCommittee.members.length) return;

    try {
      await fetch(`/api/memberships/${membershipId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayOrder: newOrder }),
      });
      loadCommitteeDetails(selectedCommittee.id);
    } catch (error) {
      console.error('Error moving member:', error);
    }
  };

  const updateMemberRole = async (membershipId: number, newRole: string) => {
    try {
      await fetch(`/api/memberships/${membershipId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });
      if (selectedCommittee) {
        loadCommitteeDetails(selectedCommittee.id);
      }
      setEditingMember(null);
    } catch (error) {
      console.error('Error updating member role:', error);
    }
  };

  const updateCommitteeName = async () => {
    if (!selectedCommittee || !editCommitteeName.trim()) return;

    try {
      await fetch(`/api/committees/${selectedCommittee.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editCommitteeName }),
      });
      loadCommittees();
      loadCommitteeDetails(selectedCommittee.id);
      setEditingCommittee(false);
    } catch (error) {
      console.error('Error updating committee name:', error);
    }
  };

  const updateMemberName = async (personId: number) => {
    if (!editMemberNameValue.trim()) return;

    try {
      await fetch(`/api/persons/${personId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editMemberNameValue }),
      });
      if (selectedCommittee) {
        loadCommitteeDetails(selectedCommittee.id);
      }
      setEditingMemberName(null);
      setEditMemberNameValue('');
    } catch (error) {
      console.error('Error updating member name:', error);
    }
  };

  const toggleCommitteeActive = async (id: number, isActive: boolean) => {
    try {
      await fetch(`/api/committees/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !isActive }),
      });
      loadCommittees();
      if (selectedCommittee?.id === id) {
        loadCommitteeDetails(id);
      }
    } catch (error) {
      console.error('Error toggling committee active status:', error);
    }
  };

  const handleSetPin = async (e: React.FormEvent) => {
    e.preventDefault();
    setPinMessage('');

    // Validate PIN format
    if (pin.length !== 6 || !/^\d+$/.test(pin)) {
      setPinMessage('PIN must be exactly 6 digits');
      return;
    }

    if (pin !== confirmPin) {
      setPinMessage('PINs do not match');
      return;
    }

    try {
      await pinService.setPin(pin, confirmPin);
      setPinMessage('Presentation PIN updated successfully!');
      setPin('');
      setConfirmPin('');
      setShowPinSection(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setPinMessage(''), 3000);
    } catch (error: any) {
      setPinMessage(error.message || 'Failed to set PIN');
    }
  };

  const handlePinChange = (value: string, setter: (value: string) => void) => {
    // Only allow digits, max 6 characters
    const sanitized = value.replace(/\D/g, '').slice(0, 6);
    setter(sanitized);
  };

  const handleAddCommittee = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionMessage('');

    if (!newCommitteeName.trim()) {
      setActionMessage('Committee name is required');
      return;
    }

    try {
      const response = await fetch('/api/committees', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCommitteeName, isActive: true }),
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        setActionMessage(error.error || 'Failed to create committee');
        return;
      }

      setActionMessage('Committee created successfully!');
      setNewCommitteeName('');
      setShowAddCommittee(false);
      loadCommittees();
      setTimeout(() => setActionMessage(''), 3000);
    } catch (error) {
      console.error('Error creating committee:', error);
      setActionMessage('Error creating committee');
    }
  };

  const handleDeleteCommittee = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This will also remove all members from this committee.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/committees/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        setActionMessage('Failed to delete committee');
        return;
      }

      setActionMessage('Committee deleted successfully!');
      loadCommittees();
      if (selectedCommittee?.id === id) {
        setSelectedCommittee(null);
      }
      setTimeout(() => setActionMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting committee:', error);
      setActionMessage('Error deleting committee');
    }
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionMessage('');

    if (!selectedCommittee) return;
    if (!newMemberName.trim()) {
      setActionMessage('Member name is required');
      return;
    }

    try {
      const response = await fetch('/api/memberships', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          committeeId: selectedCommittee.id,
          personName: newMemberName,
          role: newMemberRole,
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        setActionMessage(error.error || 'Failed to add member');
        return;
      }

      setActionMessage('Member added successfully!');
      setNewMemberName('');
      setNewMemberRole('Member');
      setShowAddMember(false);
      loadCommitteeDetails(selectedCommittee.id);
      setTimeout(() => setActionMessage(''), 3000);
    } catch (error) {
      console.error('Error adding member:', error);
      setActionMessage('Error adding member');
    }
  };

  const handleDeleteMember = async (membershipId: number, memberName: string) => {
    if (!window.confirm(`Are you sure you want to remove "${memberName}" from this committee?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/memberships/${membershipId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        setActionMessage('Failed to remove member');
        return;
      }

      setActionMessage('Member removed successfully!');
      if (selectedCommittee) {
        loadCommitteeDetails(selectedCommittee.id);
      }
      setTimeout(() => setActionMessage(''), 3000);
    } catch (error) {
      console.error('Error removing member:', error);
      setActionMessage('Error removing member');
    }
  };

  const handleClearAllData = async () => {
    if (!window.confirm('⚠️ WARNING: This will delete ALL committees and members! This action cannot be undone. Are you absolutely sure?')) {
      return;
    }

    if (!window.confirm('This is your last chance! Type YES in the next prompt to confirm.')) {
      return;
    }

    const confirmation = window.prompt('Type YES to confirm deletion of all data:');
    if (confirmation !== 'YES') {
      setActionMessage('Data deletion cancelled');
      setTimeout(() => setActionMessage(''), 3000);
      return;
    }

    try {
      const response = await fetch('/api/admin/clear-all-data', {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        setActionMessage('Failed to clear data');
        return;
      }

      const result = await response.json();
      setActionMessage(`All data cleared! Deleted ${result.deletedCommittees} committees, ${result.deletedMemberships} memberships, ${result.deletedPersons} persons.`);
      setSelectedCommittee(null);
      loadCommittees();
      setTimeout(() => setActionMessage(''), 5000);
    } catch (error) {
      console.error('Error clearing data:', error);
      setActionMessage('Error clearing data');
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div className="header-left">
          <h1>Committee Admin Panel</h1>
          {authUser && (
            <span className="auth-status">
              Logged in as {authUser.email} ({authUser.role})
            </span>
          )}
        </div>
        <div className="header-right">
          <a href="/" className="home-link">Back to Home</a>
          <button onClick={handleLogout} className="logout-button">Logout</button>
        </div>
      </header>

      <div className="admin-content">
        <div className="csv-upload-section">
          <h2>Import CSV</h2>
          <form onSubmit={handleCsvUpload}>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
            />
            <button type="submit" disabled={!csvFile}>Upload</button>
          </form>
          {uploadMessage && <p className="upload-message">{uploadMessage}</p>}
          
          <div className="danger-zone">
            <h3>⚠️ Danger Zone</h3>
            <p>Clear all committees and members to start fresh.</p>
            <button 
              className="btn-danger" 
              onClick={handleClearAllData}
              type="button"
            >
              Clear All Data
            </button>
          </div>
        </div>

        {actionMessage && (
          <div className={`action-message ${actionMessage.includes('success') || actionMessage.includes('cleared') ? 'success' : 'error'}`}>
            {actionMessage}
          </div>
        )}

        <div className="pin-management-section">
          <h2>Presentation PIN</h2>
          <p className="section-description">
            Manage the 6-digit PIN required to access the presentation mode.
          </p>
          
          {!showPinSection ? (
            <button 
              className="btn-primary"
              onClick={() => setShowPinSection(true)}
            >
              Set/Update Presentation PIN
            </button>
          ) : (
            <form onSubmit={handleSetPin} className="pin-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="pin">New PIN (6 digits)</label>
                  <input
                    id="pin"
                    type="password"
                    value={pin}
                    onChange={(e) => handlePinChange(e.target.value, setPin)}
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirmPin">Confirm PIN</label>
                  <input
                    id="confirmPin"
                    type="password"
                    value={confirmPin}
                    onChange={(e) => handlePinChange(e.target.value, setConfirmPin)}
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={pin.length !== 6 || confirmPin.length !== 6}>
                  Save PIN
                </button>
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => {
                    setShowPinSection(false);
                    setPin('');
                    setConfirmPin('');
                    setPinMessage('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
          
          {pinMessage && (
            <p className={`pin-message ${pinMessage.includes('success') ? 'success' : 'error'}`}>
              {pinMessage}
            </p>
          )}
        </div>

        <div className="committees-section">
          <div className="section-header">
            <h2>Committees ({committees.length})</h2>
            <button className="btn-primary" onClick={() => setShowAddCommittee(true)}>
              + Add Committee
            </button>
          </div>

          {showAddCommittee && (
            <form onSubmit={handleAddCommittee} className="add-form">
              <input
                type="text"
                value={newCommitteeName}
                onChange={(e) => setNewCommitteeName(e.target.value)}
                placeholder="New committee name"
                autoFocus
              />
              <button type="submit" className="btn-primary">Create</button>
              <button 
                type="button" 
                className="btn-secondary"
                onClick={() => {
                  setShowAddCommittee(false);
                  setNewCommitteeName('');
                }}
              >
                Cancel
              </button>
            </form>
          )}

          <div className="committees-list">
            {committees.map((committee) => (
              <div key={committee.id} className={`committee-item ${!committee.isActive ? 'inactive' : ''}`}>
                <div className="committee-info">
                  <span className="committee-name" onClick={() => loadCommitteeDetails(committee.id)}>
                    {committee.name} ({committee.memberCount} members)
                  </span>
                  {!committee.isActive && <span className="inactive-badge">Inactive</span>}
                </div>
                <div className="committee-actions">
                  <button onClick={() => moveCommittee(committee.id, 'up')} disabled={committee.displayOrder === 1}>
                    ↑
                  </button>
                  <button onClick={() => moveCommittee(committee.id, 'down')} disabled={committee.displayOrder === committees.length}>
                    ↓
                  </button>
                  <button onClick={() => toggleCommitteeActive(committee.id, committee.isActive)}>
                    {committee.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  <button 
                    className="btn-delete" 
                    onClick={() => handleDeleteCommittee(committee.id, committee.name)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedCommittee && (
          <div className="committee-details">
            <div className="detail-header">
              {editingCommittee ? (
                <div className="edit-committee-form">
                  <input
                    type="text"
                    value={editCommitteeName}
                    onChange={(e) => setEditCommitteeName(e.target.value)}
                    placeholder="Committee name"
                  />
                  <button onClick={updateCommitteeName}>Save</button>
                  <button onClick={() => setEditingCommittee(false)}>Cancel</button>
                </div>
              ) : (
                <>
                  <h2>{selectedCommittee.name}</h2>
                  <button onClick={() => {
                    setEditCommitteeName(selectedCommittee.name);
                    setEditingCommittee(true);
                  }}>Edit Name</button>
                </>
              )}
            </div>

            <div className="leadership">
              <p><strong>Chair:</strong> {selectedCommittee.chair || 'None'}</p>
              <p><strong>Co-Chair:</strong> {selectedCommittee.coChair || 'None'}</p>
            </div>

            <div className="section-header">
              <h3>Members ({selectedCommittee.members.length})</h3>
              <button className="btn-primary btn-sm" onClick={() => setShowAddMember(true)}>
                + Add Member
              </button>
            </div>

            {showAddMember && (
              <form onSubmit={handleAddMember} className="add-form">
                <input
                  type="text"
                  value={newMemberName}
                  onChange={(e) => setNewMemberName(e.target.value)}
                  placeholder="Member name"
                  autoFocus
                />
                <select
                  value={newMemberRole}
                  onChange={(e) => setNewMemberRole(e.target.value)}
                >
                  <option value="Member">Member</option>
                  <option value="Chair">Chair</option>
                  <option value="Co-Chair">Co-Chair</option>
                  <option value="Past Chair">Past Chair</option>
                  <option value="Other">Other</option>
                </select>
                <button type="submit" className="btn-primary">Add</button>
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => {
                    setShowAddMember(false);
                    setNewMemberName('');
                    setNewMemberRole('Member');
                  }}
                >
                  Cancel
                </button>
              </form>
            )}

            <div className="members-list">
              {selectedCommittee.members.map((member) => (
                <div key={member.id} className="member-item">
                  <div className="member-info">
                    {editingMemberName === member.id ? (
                      <div className="edit-member-name">
                        <input
                          type="text"
                          value={editMemberNameValue}
                          onChange={(e) => setEditMemberNameValue(e.target.value)}
                          placeholder="Member name"
                          className="member-name-input"
                          autoFocus
                        />
                        <button 
                          className="save-btn"
                          onClick={() => updateMemberName(member.personId)}
                        >
                          ✓
                        </button>
                        <button 
                          className="cancel-btn"
                          onClick={() => {
                            setEditingMemberName(null);
                            setEditMemberNameValue('');
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <span 
                        className="member-name clickable" 
                        onClick={() => {
                          setEditingMemberName(member.id);
                          setEditMemberNameValue(member.name);
                        }}
                        title="Click to edit name"
                      >
                        {member.name}
                      </span>
                    )}
                    {editingMember === member.id ? (
                      <select
                        value={member.role}
                        onChange={(e) => updateMemberRole(member.id, e.target.value)}
                        className="role-select"
                      >
                        <option value="Chair">Chair</option>
                        <option value="Co-Chair">Co-Chair</option>
                        <option value="Member">Member</option>
                        <option value="Past Chair">Past Chair</option>
                        <option value="Other">Other</option>
                      </select>
                    ) : (
                      <span className="member-role clickable" onClick={() => setEditingMember(member.id)} title="Click to edit role">
                        {member.role}
                      </span>
                    )}
                  </div>
                  <div className="member-actions">
                    <button onClick={() => moveMember(member.id, 'up')} disabled={member.displayOrder === 1}>
                      ↑
                    </button>
                    <button onClick={() => moveMember(member.id, 'down')} disabled={member.displayOrder === selectedCommittee.members.length}>
                      ↓
                    </button>
                    <button 
                      className="btn-delete" 
                      onClick={() => handleDeleteMember(member.id, member.name)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
